import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CustomValidators } from 'angular-custom-validators';
import { deleteField } from 'firebase/firestore';
import { catchError, lastValueFrom, merge, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { WarningDialog } from '../global-dialogs/warning/warning.component';
import { AddressValidationService } from './address-validation.service';
import { PostcodeApiService } from './postcode-api.service';

export interface AddressData {
  postal?: string | number;
  houseNumber?: string | number;
  houseNumberAddition?: string;
  streetName?: string;
  city?: string;
  addressCoordinates?: { latitude: number; longitude: number } | null;
  addressCoordinatesFor?: string | null;
  addressFingerprint?: string | null;
  location?: { _lat: number; _long: number } | null;
}

@Injectable({ providedIn: 'root' })
export class AddressValidationFlowService {
  private readonly builder = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly addressValidationService = inject(AddressValidationService);
  private readonly postcodeApiService = inject(PostcodeApiService);

  addControls(form: UntypedFormGroup): void {
    form.addControl(
      'postal',
      this.builder.control('', [Validators.required, CustomValidators.postal]),
    );
    form.addControl(
      'houseNumber',
      this.builder.control('', [
        Validators.required,
        CustomValidators.numberInput(false, false, 0),
      ]),
    );
    form.addControl('houseNumberAddition', this.builder.control(''));
    form.addControl('streetName', this.builder.control('', Validators.required));
    form.addControl('city', this.builder.control('', Validators.required));
  }

  connect(form: UntypedFormGroup, destroyRef: DestroyRef) {
    const isLookingUp = signal(false);
    const addressNotFound = signal(false);
    const reset$ = new Subject<void>();
    const setAddressFieldsDisabled = (disabled: boolean) => {
      ['streetName', 'city'].forEach((field) => form.controls[field].disable({ emitEvent: false }));
      if (!disabled) {
        ['streetName', 'city'].forEach((field) =>
          form.controls[field].enable({ emitEvent: false }),
        );
      }
    };
    const { postal, houseNumber } = form.controls;
    setAddressFieldsDisabled(!(postal.valid && houseNumber.valid));

    const subscription = merge(
      form.controls.postal.valueChanges,
      form.controls.houseNumber.valueChanges,
      form.controls.houseNumberAddition.valueChanges,
    )
      .pipe(
        takeUntilDestroyed(destroyRef),
        switchMap(() => {
          const { postal, houseNumber } = form.controls;
          const isValid = postal.valid && houseNumber.valid;

          addressNotFound.set(false);
          if (!isValid) {
            isLookingUp.set(false);
            setAddressFieldsDisabled(true);
            return of(null);
          }

          isLookingUp.set(true);
          setAddressFieldsDisabled(true);
          return timer(600).pipe(
            switchMap(() =>
              this.postcodeApiService.getAddress(postal.value, houseNumber.value).pipe(
                catchError(() => {
                  isLookingUp.set(false);
                  addressNotFound.set(true);
                  setAddressFieldsDisabled(false);
                  return of(null);
                }),
              ),
            ),
            takeUntil(reset$),
          );
        }),
      )
      .subscribe({
        next: (result) => {
          isLookingUp.set(false);
          const { postal, houseNumber } = form.controls;

          if (result) {
            form.patchValue({ streetName: result.street, city: result.city }, { emitEvent: false });
            setAddressFieldsDisabled(false);
          } else if (postal.valid && houseNumber.valid) {
            addressNotFound.set(true);
            setAddressFieldsDisabled(false);
          }
        },
        error: () => {
          isLookingUp.set(false);
          addressNotFound.set(true);
          setAddressFieldsDisabled(false);
        },
      });

    return {
      isLookingUp,
      addressNotFound,
      reset: () => {
        reset$.next();
        isLookingUp.set(false);
        addressNotFound.set(false);
        const { postal, houseNumber } = form.controls;
        setAddressFieldsDisabled(!(postal.valid && houseNumber.valid));
      },
      disconnect: () => subscription.unsubscribe(),
    };
  }

  async resolveAddress(
    saveObj: Record<string, any>,
    originalUser: Record<string, any> = {},
    usesAutomaticPlanning: boolean,
    isExistingUser: boolean,
  ): Promise<Record<string, any> | null> {
    const resolvedSaveObj = { ...saveObj };
    let addressChanged = true;

    if (isExistingUser && originalUser) {
      const isSame = (newVal: any, oldVal: any, isPostal = false) => {
        let normalizedNewValue = newVal?.['_methodName'] === 'deleteField' ? '' : (newVal ?? '');
        let normalizedOldValue = oldVal ?? '';
        if (isPostal) {
          normalizedNewValue = normalizedNewValue.toString().replace(/\s/g, '');
          normalizedOldValue = normalizedOldValue.toString().replace(/\s/g, '');
        }
        return (
          normalizedNewValue.toString().trim().toUpperCase() ===
          normalizedOldValue.toString().trim().toUpperCase()
        );
      };

      const addressIsUnchanged =
        isSame(resolvedSaveObj.streetName, originalUser.streetName) &&
        isSame(resolvedSaveObj.houseNumber, originalUser.houseNumber) &&
        isSame(resolvedSaveObj.houseNumberAddition, originalUser.houseNumberAddition) &&
        isSame(resolvedSaveObj.postal, originalUser.postal, true) &&
        isSame(resolvedSaveObj.city, originalUser.city);

      const normalizedAddress = this.addressValidationService.normalizeAddressForComparison(
        `${resolvedSaveObj.streetName ?? ''} ${resolvedSaveObj.houseNumber ?? ''} ${resolvedSaveObj.houseNumberAddition ?? ''}, ${resolvedSaveObj.postal ?? ''} ${resolvedSaveObj.city ?? ''}`,
      );
      const hasCachedCoordinates = !!originalUser.addressCoordinates;
      const fingerprintMatches =
        !originalUser.addressCoordinatesFor ||
        originalUser.addressCoordinatesFor === normalizedAddress;

      if (addressIsUnchanged && hasCachedCoordinates && fingerprintMatches) {
        addressChanged = false;
        resolvedSaveObj.addressCoordinates = { ...originalUser.addressCoordinates };
        resolvedSaveObj.addressCoordinatesFor =
          originalUser.addressCoordinatesFor ?? normalizedAddress;
        resolvedSaveObj.addressFingerprint = normalizedAddress;
      }
    }

    if (addressChanged) {
      const addressString = `${resolvedSaveObj.streetName} ${resolvedSaveObj.houseNumber} ${resolvedSaveObj.houseNumberAddition ?? ''}, ${resolvedSaveObj.postal} ${resolvedSaveObj.city}`;
      const addressValidationResult =
        await this.addressValidationService.validateAddress(addressString);

      if (!addressValidationResult.isValid && !addressValidationResult.requiresConfirmation) {
        const dialogType = usesAutomaticPlanning
          ? 'addressNotValidUser'
          : 'addressNotValidUserOptional';

        const result = await lastValueFrom(
          this.dialog
            .open(WarningDialog, {
              width: '600px',
              data: {
                dialogType,
                optionalData: {
                  confirmButtonText: 'Ja, toch opslaan',
                },
              },
            })
            .afterClosed(),
        );

        if (!result) return null;
        resolvedSaveObj.addressCoordinates = deleteField();
        resolvedSaveObj.addressCoordinatesFor = deleteField();
        resolvedSaveObj.addressFingerprint = deleteField();
        resolvedSaveObj.location = deleteField();
      } else if (addressValidationResult.requiresConfirmation) {
        const result = await lastValueFrom(
          this.dialog
            .open(WarningDialog, {
              width: '600px',
              data: {
                dialogType: 'addressLowConfidence',
                optionalData: {
                  enteredAddress: addressString,
                  candidateAddress: [
                    addressValidationResult.street,
                    addressValidationResult.housenumber,
                    addressValidationResult.postal,
                    addressValidationResult.city,
                  ]
                    .filter(Boolean)
                    .join(' '),
                  coordinates: addressValidationResult.coordinates,
                },
              },
            })
            .afterClosed(),
        );

        if (!result) return null;
      }

      if (addressValidationResult.isValid || addressValidationResult.requiresConfirmation) {
        const validatedPostal = addressValidationResult.postal ?? resolvedSaveObj.postal;
        const validatedCoordinates = addressValidationResult.coordinates;
        resolvedSaveObj.streetName = addressValidationResult.street ?? null;
        resolvedSaveObj.postal = validatedPostal
          ? validatedPostal.replace(/\s/g, '').toUpperCase()
          : null;
        resolvedSaveObj.city = addressValidationResult.city ?? null;
        if (validatedCoordinates) {
          resolvedSaveObj.location = {
            _lat: validatedCoordinates.latitude,
            _long: validatedCoordinates.longitude,
          };
          resolvedSaveObj.addressCoordinates = {
            latitude: validatedCoordinates.latitude,
            longitude: validatedCoordinates.longitude,
          };
          resolvedSaveObj.addressCoordinatesFor =
            this.addressValidationService.normalizeAddressForComparison(
              `${addressValidationResult.street ?? resolvedSaveObj.streetName ?? ''} ${resolvedSaveObj.houseNumber ?? ''} ${resolvedSaveObj.houseNumberAddition ?? ''}, ${validatedPostal ?? resolvedSaveObj.postal ?? ''} ${addressValidationResult.city ?? resolvedSaveObj.city ?? ''}`,
            );
          resolvedSaveObj.addressFingerprint = resolvedSaveObj.addressCoordinatesFor;
        }
      }
    }

    return resolvedSaveObj;
  }
}
